import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  Clock,
  Gift,
  Star,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const InterstitialAd = ({
  isVisible,
  onClose,
  onAdClick,
  trigger = 'transition', // 'transition', 'action-complete', 'time-based'
  context = '' // What action was just completed
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [canClose, setCanClose] = useState(false);
  const [animationClass, setAnimationClass] = useState('');

  // Contextual interstitial ads based on user actions
  const interstitialAds = {
    'group-created': {
      id: 'interstitial-success-1',
      type: 'congratulations',
      title: '🎉 Félicitations !',
      subtitle: 'Votre groupe a été créé avec succès',
      description: 'Maximisez le potentiel de votre groupe avec nos outils premium',
      mainImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiByeD0iMTYiIGZpbGw9IiNmMGZkZjQiLz48Y2lyY2xlIGN4PSIxMDAiIGN5PSI4MCIgcj0iNDAiIGZpbGw9IiMxMGI5ODEiLz48cGF0aCBkPSJtODUgODAgMTAgMTAgMjAtMjAiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNCIgZmlsbD0ibm9uZSIvPjx0ZXh0IHg9IjEwMCIgeT0iMTU1IiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iIzA1OWY1MSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+R3JvdXBlIENyw6nDqSE8L3RleHQ+PC9zdmc+',
      offer: 'Analyse Gratuite des Performances',
      features: ['Statistiques avancées', 'Rapports mensuels', 'Conseils personnalisés'],
      cta: 'Découvrir Premium',
      brand: 'Likico Pro',
      backgroundColor: '#f0fdf4',
      primaryColor: '#10b981',
      closeDelay: 3 // User can close after 3 seconds
    },
    'payment-completed': {
      id: 'interstitial-invest-1',
      type: 'investment-opportunity',
      title: '💰 Optimisez vos Gains',
      subtitle: 'Paiement effectué avec succès',
      description: 'Investissez intelligemment vos contributions avec nos partenaires certifiés',
      mainImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiByeD0iMTYiIGZpbGw9IiNmZmY3ZWQiLz48cGF0aCBkPSJtNTAgMTUwIDMwLTQwIDMwIDI0IDMwLTM0IDMwIDI0IDMwLTIwIiBzdHJva2U9IiNmNTk1MGIiIHN0cm9rZS13aWR0aD0iNCIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjE3MCIgY3k9IjUwIiByPSIxMCIgZmlsbD0iI2Y1OWUwYiIvPjx0ZXh0IHg9IjEwMCIgeT0iMTc1IiBmb250LXNpemU9IjE0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iI2Q5NzcwNCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW52ZXN0aXNzZW1lbnQgSW50ZWxsaWdlbnQ8L3RleHQ+PC9zdmc+',
      offer: 'Rendement jusqu\'à 12% annuel',
      features: ['Placement automatique', 'Risque contrôlé', 'Liquidité garantie'],
      cta: 'Commencer à Investir',
      brand: 'Equity Bank',
      backgroundColor: '#fff7ed',
      primaryColor: '#f59e0b',
      closeDelay: 4
    },
    'member-joined': {
      id: 'interstitial-insurance-1',
      type: 'protection-offer',
      title: '🛡️ Protégez votre Épargne',
      subtitle: 'Nouveau membre dans votre groupe',
      description: 'Sécurisez vos contributions et celles de votre groupe avec notre assurance dédiée',
      mainImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiByeD0iMTYiIGZpbGw9IiNmYWY5ZmYiLz48cGF0aCBkPSJNMTAwIDQwIEw2MCA4MCBMNjAgMTQwIEw2MCA1MCBMMTAwIDQwIEwxNDAgNTAgTDE0MCA4MCBMMTQ5IDE0MCBMMTQ5IDgwIEwxNDkgNTB6IiBmaWxsPSIjMzk2NmY5IiBzdHJva2U9IiMxZTQwYWYiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjEwMCIgY3k9IjkwIiByPSIxNSIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJtOTMgOTAgNSA1IDEwLTEwIiBzdHJva2U9IiMzOTY2ZjkiIHN0cm9rZS13aWR0aD0iMyIgZmlsbD0ibm9uZSIvPjx0ZXh0IHg9IjEwMCIgeT0iMTc1IiBmb250LXNpemU9IjE0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iIzFkNGVkOCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UHJvdGVjdGlvbiBUb3RhbGU8L3RleHQ+PC9zdmc+',
      offer: 'Premier mois gratuit',
      features: ['Couverture 100%', 'Remboursement rapide', 'Support 24/7'],
      cta: 'Découvrir l\'Assurance',
      brand: 'Jubilee Insurance',
      backgroundColor: '#faf9ff',
      primaryColor: '#3b82f6',
      closeDelay: 4
    },
    'transition': {
      id: 'interstitial-default-1',
      type: 'brand-awareness',
      title: '✨ Découvrez Nos Partenaires',
      subtitle: 'Services recommandés pour vous',
      description: 'Explorez les services financiers qui complètent parfaitement votre expérience Likico',
      mainImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiByeD0iMTYiIGZpbGw9IiNmOWZhZmIiLz48Y2lyY2xlIGN4PSI3MCIgY3k9IjcwIiByPSIyNSIgZmlsbD0iIzg1NTVkNCIvPjxjaXJjbGUgY3g9IjEzMCIgY3k9IjcwIiByPSIyNSIgZmlsbD0iIzM5MzNmMyIvPjxjaXJjbGUgY3g9IjEwMCIgY3k9IjEyMCIgcj0iMjUiIGZpbGw9IiMwNjYzZjciLz48cGF0aCBkPSJNNzAgNzAgTDEzMCA3MCIgc3Ryb2tlPSIjYzhlMWVmIiBzdHJva2Utd2lkdGg9IjQiLz48cGF0aCBkPSJNNzAgNzAgTDEwMCAxMjAiIHN0cm9rZT0iI2M4ZTFlZiIgc3Ryb2tlLXdpZHRoPSI0Ii8+PHBhdGggZD0iTTEzMCA3MCBMMTM9IDEyMCIgc3Ryb2tlPSIjYzhlMWVmIiBzdHJva2Utd2lkdGg9IjQiLz48dGV4dCB4PSIxMDAiIHk9IjE3NSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiMzNzQ5NTEiIHRleHQtYW5jaG9yPSJtaWRkbGUiPsOJY29zeXN0w6htZSBQYXJ0ZW5haXJlPC90ZXh0Pjwvc3ZnPg==',
      offer: 'Services exclusifs',
      features: ['Offres privilégiées', 'Support dédié', 'Intégration fluide'],
      cta: 'Explorer',
      brand: 'Partenaires Likico',
      backgroundColor: '#f9fafb',
      primaryColor: '#6366f1',
      closeDelay: 5
    }
  };

  useEffect(() => {
    if (isVisible && context) {
      const ad = interstitialAds[context] || interstitialAds['transition'];
      setCurrentAd(ad);
      setCountdown(ad.closeDelay);
      setAnimationClass('animate-in');
      
      // Countdown timer
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanClose(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isVisible, context]);

  const handleClose = () => {
    if (canClose || countdown === 0) {
      setAnimationClass('animate-out');
      setTimeout(() => {
        onClose();
        setAnimationClass('');
      }, 300);
    }
  };

  const handleAdClick = () => {
    if (onAdClick && currentAd) {
      onAdClick(currentAd);
    }
  };

  const handleCtaClick = () => {
    if (onAdClick && currentAd) {
      onAdClick(currentAd, 'cta');
    }
  };

  if (!isVisible || !currentAd) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div 
        className={`
          relative max-w-sm mx-4 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300
          ${animationClass === 'animate-in' ? 'scale-100 opacity-100' : 
            animationClass === 'animate-out' ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
        `}
        style={{ backgroundColor: currentAd.backgroundColor }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={!canClose && countdown > 0}
          className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            canClose || countdown === 0
              ? 'bg-white shadow-lg hover:bg-gray-50'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
          aria-label="Fermer la publicité"
        >
          {canClose || countdown === 0 ? (
            <X className="w-5 h-5 text-gray-600" />
          ) : (
            <span className="text-sm font-bold text-gray-600">{countdown}</span>
          )}
        </button>

        {/* Sponsor Badge */}
        <div className="absolute top-4 left-4 z-10">
          <span className="px-3 py-1 bg-white bg-opacity-90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
            Sponsorisé • {currentAd.brand}
          </span>
        </div>

        {/* Main Content */}
        <div className="p-8 text-center">
          {/* Header Image/Icon */}
          <div className="mb-6">
            <img 
              src={currentAd.mainImage} 
              alt={currentAd.title}
              className="w-32 h-32 mx-auto object-contain"
            />
          </div>

          {/* Title and Subtitle */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {currentAd.title}
            </h2>
            <p className="text-lg text-gray-700 mb-1">
              {currentAd.subtitle}
            </p>
            <p className="text-gray-600">
              {currentAd.description}
            </p>
          </div>

          {/* Special Offer */}
          {currentAd.offer && (
            <div 
              className="mb-6 p-4 rounded-xl border-2 border-dashed"
              style={{ 
                borderColor: currentAd.primaryColor,
                backgroundColor: currentAd.primaryColor + '10'
              }}
            >
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Gift className="w-5 h-5" style={{ color: currentAd.primaryColor }} />
                <span className="font-semibold" style={{ color: currentAd.primaryColor }}>
                  Offre Spéciale
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {currentAd.offer}
              </p>
            </div>
          )}

          {/* Features */}
          {currentAd.features && (
            <div className="mb-8 space-y-3">
              {currentAd.features.map((feature, index) => (
                <div key={index} className="flex items-center justify-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleCtaClick}
              className="w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 transform hover:scale-105 shadow-lg group"
              style={{ backgroundColor: currentAd.primaryColor }}
            >
              <span>{currentAd.cta}</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={handleClose}
              className="w-full py-3 px-6 text-gray-600 hover:text-gray-800 transition-colors font-medium"
            >
              {canClose || countdown === 0 ? 'Peut-être plus tard' : `Fermer dans ${countdown}s`}
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Shield className="w-4 h-4" />
                <span>Sécurisé</span>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4" />
                <span>Partenaire certifié</span>
              </div>
              <div className="flex items-center space-x-1">
                <Zap className="w-4 h-4" />
                <span>Installation rapide</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gradient */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-20 opacity-20"
          style={{
            background: `linear-gradient(to top, ${currentAd.primaryColor}, transparent)`
          }}
        />
      </div>
    </div>
  );
};

export default InterstitialAd;